import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Svg from './Svg';
import classNames from 'classnames';
import { addUniqueIdsToArrayItems, open, close, keyCode } from '../../helpers';

const Dropdown = ({
  label,
  options: propOptions,
  name,
  initialOption,
  onChange,
  className,
  errorMessage
}) => {
  const componentRef = useRef(null);
  const triggerEl = useRef(null);
  const listEl = useRef(null);
  const search = useRef('');
  const searchTimeout = useRef(null);
  const { current: options } = useRef(addUniqueIdsToArrayItems(propOptions));
  const [inFocusElement, setInFocusElement] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const handleTriggerKeyEvents = e => {
    const key = e.which || e.keyCode;

    switch (key) {
      case keyCode.DOWN:
      case keyCode.UP:
        e.preventDefault();
        setIsOpen(true);
        setInFocusElement(listEl.current);
        if (!isOpen) open({ element: listEl.current, duration: 0.6 });
        break;
      case keyCode.ESC:
        if (isOpen) close({ element: listEl.current, duration: 0.6 });
        return setIsOpen(false);
      default:
        findItemToFocus(key);
        break;
    }
  };

  const handleTriggerClickEvent = e => {
    e.stopPropagation();
    listEl.current.removeAttribute('style');
    setIsOpen(prev => !prev);
    !isOpen
      ? open({ element: listEl.current, duration: 0.6 })
      : close({ element: listEl.current, duration: 0.6 });
  };

  const handleClickOutside = event => {
    if (componentRef.current && !componentRef.current.contains(event.target)) {
      listEl.current.style.zIndex = '-1'
      close({ element: listEl.current, duration: 0.6 });
      setIsOpen(false);
    }
  };

  const handleListKeyEvents = e => {
    e.preventDefault();
    const key = e.which || e.keyCode;

    switch (key) {
      case keyCode.ESC:
      case keyCode.RETURN:
        setIsOpen(false);
        close({ element: listEl.current, duration: 0.6 });
        setInFocusElement(triggerEl.current);

        if (key === keyCode.ESC) {
          handleOnChange({ index: selectedIndex, triggerChange: false });
        }
        if (key === keyCode.RETURN) {
          handleOnChange({ index: focusedOptionIndex });
        }
        break;
      case keyCode.DOWN:
      case keyCode.UP:
        const deltas = { [keyCode.DOWN]: 1, [keyCode.UP]: -1 };
        const startOfArray = 0;
        const endOfArray = options.length - 1;
        const nextItem = focusedOptionIndex + deltas[key];

        if (nextItem < startOfArray) {
          focusItem(endOfArray);
        } else if (nextItem > endOfArray) {
          focusItem(startOfArray);
        } else {
          focusItem(nextItem);
        }
        break;
      default:
        findItemToFocus(key);
        break;
    }
  };

  const findItemToFocus = key => {
    const character = String.fromCharCode(key).trim();

    if (!character) return;

    if (!isOpen) {
      setIsOpen(true);
      open({ element: listEl.current, duration: 0.6 });
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    search.current += character;

    options.find((option, index) => {
      if (option.label.toLowerCase().startsWith(search.current.toLowerCase())) {
        setSelectedIndex(index);
        focusItem(index);
        return true;
      }
    });

    searchTimeout.current = setTimeout(() => {
      search.current = '';
    }, 500);
  };

  const handleOnChange = ({ index, triggerChange = true }) => {
    if (isOpen) close({ element: listEl.current, duration: 0.6 });
    setIsOpen(false);
    setSelectedIndex(index);
    focusItem(index);
    triggerChange && onChange(options[index]);
  };

  const focusItem = index => {
    setFocusedOptionIndex(index);

    const { value } = options[index];

    if (!value || !listEl.current) return;

    const elementToFocus = listEl.current.querySelector(
      `[data-value='${value}']`
    );

    if (listEl.current.scrollHeight > listEl.current.clientHeight) {
      const scrollBottom =
        listEl.current.clientHeight + listEl.current.scrollTop;
      const elementBottom =
        elementToFocus.offsetTop + elementToFocus.offsetHeight;
      if (elementBottom > scrollBottom) {
        listEl.current.scrollTop = elementBottom - listEl.current.clientHeight;
      } else if (elementToFocus.offsetTop < listEl.current.scrollTop) {
        listEl.current.scrollTop = elementToFocus.offsetTop;
      }
    }
  };

  useEffect(() => {
    inFocusElement?.focus();
  }, [inFocusElement]);

  useEffect(() => {
    const initialSelectedIndex = options.findIndex(
      ({ value }) => initialOption && initialOption.value === value
    );
    if (initialSelectedIndex >= 0) {
      setSelectedIndex(initialSelectedIndex);
      setFocusedOptionIndex(initialSelectedIndex);
    }
    window.addEventListener('mousedown', e => handleClickOutside(e));
    return () => {
      window.removeEventListener('mousedown', e => handleClickOutside(e));
    };
  }, [initialOption, options]);

  return (
    <>
      <div
        className={classNames(['dropdown', className], { open: isOpen })}
        ref={componentRef}
      >
        <button
          className="dropdown__trigger"
          aria-haspopup="true"
          id={`${name}_button`}
          onClick={handleTriggerClickEvent}
          onKeyDown={handleTriggerKeyEvents}
          ref={triggerEl}
        >
          <span>{label}</span>
          {options[selectedIndex].label}
          <Svg name="chevron-right" altText="toggle dropdown"/>
        </button>
        <ul
          id={`${name}_list`}
          tabIndex="-1"
          role="listbox"
          aria-labelledby={`${name}_button`}
          aria-activedescendant={options[selectedIndex].id}
          className="dropdown__list"
          onKeyDown={handleListKeyEvents}
          ref={listEl}
        >
          {options.map((option, index) => (
            <li
              key={option.id}
              role="option"
              id={option.id}
              data-value={option.value}
              onClick={() => handleOnChange({ index })}
              aria-selected={selectedIndex === index}
              className={classNames('dropdown__list__option', {
                focused: index === focusedOptionIndex
              })}
            >
              {option.label}
              <span className="dropdown__list__option-decorator">
                <span></span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      {errorMessage && (
        <p className="dropdown__error-message">{errorMessage}</p>
      )}
    </>
  );
};

Dropdown.propTypes = {
  className: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  initialOption: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  }),
  onChange: PropTypes.func,
  errorMessage: PropTypes.string
};

export default Dropdown;
